import { useState } from "react";
import { ChevronDown } from "lucide-react";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import { Card } from "@/components/ui/card.tsx";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible.tsx";
import { MarkdownContent } from "@/components/MarkdownContent.tsx";
import { cn } from "@/lib/utils.ts";

interface FaqItemData {
  question: string;
  answer: string;
}

interface FaqGroupData {
  title: string;
  subtitle?: string;
  items: FaqItemData[];
}

interface FaqProps {
  faq_data: { groups: FaqGroupData[] };
}

function FAQItem({ item }: { item: FaqItemData }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="gap-0 py-0">
        <h3>
          <CollapsibleTrigger className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/50 transition-colors rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2">
            <span className="text-lg font-medium text-foreground pr-4">
              {item.question}
            </span>
            <ChevronDown
              className={cn(
                "size-5 text-muted-foreground shrink-0 transition-transform duration-200",
                isOpen && "rotate-180",
              )}
            />
          </CollapsibleTrigger>
        </h3>

        <CollapsibleContent>
          <div className="border-t border-border px-6 py-4">
            <MarkdownContent content={item.answer} />
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function FAQGroup({
  group,
  showDivider,
}: {
  group: FaqGroupData;
  showDivider: boolean;
}) {
  return (
    <div>
      {showDivider && <hr className="border-border mb-6" />}
      <div className="space-y-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {group.title}
          </h2>
          {group.subtitle && (
            <p className="mt-0.5 text-sm text-muted-foreground">
              {group.subtitle}
            </p>
          )}
        </div>

        {group.items.map((item) => (
          <FAQItem key={item.question} item={item} />
        ))}
      </div>
    </div>
  );
}

export default function Faq({ faq_data }: FaqProps) {
  return (
    <BizminerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Frequently Asked Questions
          </h1>
          <p className="mt-1 text-muted-foreground">
            Find answers about the AI Maturity Index and how to use the Vitelis
            Platform.
          </p>
        </div>

        {faq_data.groups.map((group, index) => (
          <FAQGroup
            key={group.title}
            group={group}
            showDivider={index > 0}
          />
        ))}
      </div>
    </BizminerLayout>
  );
}
